// src/pages/GroupedApplicationsList.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from '../api/axios';
import './GroupedApplicationsList.scoped.css';
import ApplicationView from './ApplicationView';

export default function GroupedApplicationsList({ locale = 'kz' }) {
  // ===== data =====
  const [groups, setGroups] = useState([]);
  const [meta, setMeta] = useState({ total: 0, per_page: 5, current_page: 1, last_page: 1 });
  const [err, setErr] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // ===== ui =====
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({ search: '', publisher: '', decision: 'all' });
  const [expanded, setExpanded] = useState({});
  const [approvingGroupId, setApprovingGroupId] = useState(null);
  const [selectedViewId, setSelectedViewId] = useState(null);
  const [selectedDecisionId, setSelectedDecisionId] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [isGosService, setIsGosService] = useState(false);

  const publishers = useRef([]); // список издательств (не мигать при softRefresh)
  const lastScrollRef = useRef(0);

  // ===== memo =====
  const isDirector = useMemo(
    () => ['директор', 'director'].includes((userRole || '').toLowerCase()),
    [userRole]
  );
  const langHeader = useMemo(() => (locale === 'kz' ? 'kk' : locale), [locale]);
  const hasActiveFilters = useMemo(
    () => Boolean(filters.search || filters.publisher || filters.decision !== 'all'),
    [filters]
  );
  const anyOpen = useMemo(() => groups.some((g) => expanded[g.id]), [groups, expanded]);

  const t = useMemo(
    () =>
      (locale === 'kz'
        ? {
            title: 'Жаңа Өтінімдер',
            filters: 'Сүзгілер',
            searchGroup: 'Топ атауы бойынша іздеу',
            publisherLabel: 'Баспа (атауы)',
            apply: 'Қолдану',
            reset: 'Тазарту',
            close: 'Жабу',
            expandAll: 'Барлығын ашу',
            collapseAll: 'Барлығын жабу',
            rejectedExpertise: 'Сараптамаға қабылданбады',
            apps: 'Өтінімдер',
            view: 'Толық көру',
            approve: 'Растау',
            accepted: 'Қабылданды',
            returned: 'Қайтарылды',
            noDecision: 'Шешім жоқ',
            book: 'Оқу басылымы',
            grade: 'Сынып',
            lang: 'Әзірлеу тілі',
            category: 'Санат',
            service: 'Қызмет түрі',
            publisher: 'Баспа',
            status: 'Күйі',
            date: 'Күні',
            loading: 'Жүктелуде...',
            error: 'Қате орын алды',
            forbidden: 'Қолжетімсіз',
            makeDecision: 'Қабылдау туралы шешім',
            application: 'Өтініш',
            confirm: 'Сіз шынымен осы ОӘК қабылдамақсыз ба?',
            success: 'ОӘК сәтті қабылданды!',
            any: 'Барлығы',
            decision_warning: 'Барлық өтінімдерге шешім шығарылмаған!',
            needs_reapproval: '♻️ Қайта қол қою керек',
            decisionNeeded: 'Шешім қажет',
            rule_new_no_decision: 'Жаңа өтініште шешім жоқ',
            rule_back_return_block: 'Өңдеуден келген: шешім қайтарылды → растауға болмайды',
            rule_new_only_acc_ret: 'Жаңа: тек Қабылданды/Қайтарылды рұқсат',
            rule_back_only_noexp: 'Өңдеуден келген: тек «Сараптамаға қабылданбады» рұқсат',
            rule_not_match: 'Ережеге сай емес',
          }
        : {
            title: 'Новые заявки',
            filters: 'Фильтры',
            searchGroup: 'Поиск по названию группы',
            publisherLabel: 'Издательство (имя)',
            apply: 'Применить',
            reset: 'Сбросить',
            close: 'Закрыть',
            expandAll: 'Развернуть все',
            collapseAll: 'Свернуть все',
            rejectedExpertise: 'Не допущен(а) на экспертизу',
            apps: 'Заявок',
            view: 'Просмотр',
            approve: 'Подтвердить',
            accepted: 'Принято',
            returned: 'Возвращено',
            noDecision: 'Нет решения',
            book: 'Учебное издание',
            grade: 'Класс',
            lang: 'Язык разработки',
            category: 'Категория',
            service: 'Тип услуги',
            publisher: 'Издательство',
            status: 'Статус',
            date: 'Дата',
            loading: 'Загрузка...',
            error: 'Произошла ошибка',
            forbidden: 'Недоступно',
            makeDecision: 'Решение о приеме',
            application: 'Заявка',
            confirm: 'Вы действительно хотите подтвердить это УМК?',
            success: 'УМК успешно подтверждена!',
            any: 'Все',
            decision_warning: 'Не по всем заявкам принято решение!',
            needs_reapproval: '♻️ Требуется повторное утверждение',
            decisionNeeded: 'Нужно решение',
            rule_new_no_decision: 'В новой заявке нет решения',
            rule_back_return_block: 'После доработки: решение возвращено → нельзя утверждать',
            rule_new_only_acc_ret: 'Новая: допускаются только Принято/Возвращено',
            rule_back_only_noexp: 'После доработки: допускается только «Не допущен на экспертизу»',
            rule_not_match: 'Не соответствует правилам',
          }),
    [locale]
  );

  // ===== helpers =====
  const getStatusKey = (s) => {
    const raw = typeof s === 'object'
      ? (locale === 'kz' ? s?.name_kz : s?.name_ru) || s?.name_kz || s?.name_ru || ''
      : s || '';
    const x = String(raw).toLowerCase();
    if (x.includes('жаңа') || x.includes('нов')) return 'new';
    if (x.includes('өңдеуден') || x.includes('доработ')) return 'back';
    if (x.includes('сараптамада') || x.includes('экспертиз')) return 'in_review';
    if (x.includes('қабылдан') || x.includes('принят')) return 'accepted';
    if (x.includes('қайтар') || x.includes('возврат')) return 'returned';
    return 'other';
  };

  const getDecisionKey = (d) => {
    const raw = typeof d === 'object'
      ? (locale === 'kz' ? d?.name_kz : d?.name_ru) || d?.code || d?.name_kz || d?.name_ru || ''
      : d || '';
    const x = String(raw).toLowerCase().trim();
    if (['reject_expertise', 'rejectexpertise', 'reject_expert', 'reject'].includes(x)) return 'no_expertise';
    if (/сараптамаға.*қабылданба/.test(x) || /қабылданба/.test(x)) return 'no_expertise';
    if (/не\s*допущ/.test(x)) return 'no_expertise';
    if (/(^|[\s_])accept(ed)?($|\s)/.test(x) || /қабылданд/.test(x) || /принят/.test(x)) return 'accept';
    if (/(^|[\s_])return(ed)?($|\s)/.test(x) || /қайтарыл/.test(x) || /возвращен/.test(x)) return 'return';
    if (!x) return '';
    return x;
  };

  const getDecisionLabel = (d) => {
    const k = getDecisionKey(d);
    if (k === 'accept') return t.accepted;
    if (k === 'return') return t.returned;
    if (k === 'no_expertise') return t.rejectedExpertise;
    return t.noDecision;
  };

  const classifyByStatusDecision = (a) => {
    const k = getStatusKey(a.status);
    const d = getDecisionKey(a.decision);
    const needsDecision =
      (k === 'new'  && !d) ||
      (k === 'back' && (!d || d === 'return'));
    return {
      isAccepted:  k === 'accepted' || (k === 'new' && d === 'accept') || (k === 'back' && d === 'accept'),
      isReturned:  k === 'returned' || (k === 'new' && d === 'return'),
      isNoExpertise: d === 'no_expertise',
      needsDecision,
    };
  };

  const getStatusLabel = (s) => {
    const k = getStatusKey(s);
    const dict = locale === 'kz'
      ? { new: 'Жаңа өтінім', back: 'Өңдеуден келген', in_review: 'Сараптамаға қабылданды', accepted: 'Қабылданды', returned: 'Өңдеуге қайтарылды' }
      : { new: 'Новая заявка', back: 'После доработки', in_review: 'Принято на экспертизу', accepted: 'Принято', returned: 'Возвращено' };
    if (dict[k]) return dict[k];
    if (typeof s === 'object') return (locale === 'kz' ? s?.name_kz : s?.name_ru) || s?.name_kz || s?.name_ru || '';
    return s || '-';
  };

  const getStatusBadgeClass = (s) => {
    const k = getStatusKey(s);
    return (
      {
        new: 'badge--new',
        back: 'badge--back',
        in_review: 'badge--review',
        accepted: 'badge--accepted',
        returned: 'badge--returned',
      }[k] || 'badge--neutral'
    );
  };

  const needsDecision = (app) => {
    const dk = getDecisionKey(app.decision);
    const sk = getStatusKey(app.status);
    if (dk === 'accept') return false;
    if (sk === 'returned' && dk === 'return') return false;
    return true;
  };

  const isAppEligibleForApproval = (app) => {
    const statusKey = getStatusKey(app.status);
    const decKey = getDecisionKey(app.decision);
    if (statusKey === 'accepted') return true;
    if (statusKey === 'returned') return false;
    if (statusKey === 'new')  return !!decKey && (decKey === 'accept' || decKey === 'return');
    if (statusKey === 'back') return decKey !== 'return' && (decKey === 'no_expertise' || decKey === 'accept');
    return false;
  };

  const getGroupSortDate = (g) => {
    const fromGroup = g?.created_at ? new Date(g.created_at).getTime() : 0;
    if (fromGroup) return fromGroup;
    const apps = Array.isArray(g?.applications) ? g.applications : [];
    const maxApp = apps.reduce((max, a) => {
      const t = a?.created_at ? new Date(a.created_at).getTime() : 0;
      return t > max ? t : max;
    }, 0);
    return maxApp || 0;
  };

  const normalizePayload = (res) => {
    const p = res?.data ?? {};
    if (Array.isArray(p)) return { items: p, meta: null };
    return { items: Array.isArray(p.data) ? p.data : [], meta: p.meta || null };
  };

  // ===== profile =====
  const fetchUserProfile = useCallback(async () => {
    try {
      const res = await axios.get('/internal/profile', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Accept-Language': langHeader },
      });
      setUserRole((res.data?.role?.name || '').toLowerCase());
      const depName = (res.data?.department?.name || '').toLowerCase();
      const isGos =
        depName === 'сараптаманы жоспарлау және мемлекеттік қызметтер бөлімі' ||
        depName.includes('мемлекеттік қызмет') ||
        depName.includes('гос');
      setIsGosService(isGos);
    } catch {}
  }, [langHeader]);

  // ===== fetch =====
  const fetchGroups = useCallback(
    async (page = 1, opts = {}) => {
      const { preserveExpanded = false, preserveScroll = false } = opts;

      if (preserveScroll) lastScrollRef.current = window.scrollY;

      if (!preserveExpanded && !preserveScroll) setInitialLoading(true);
      else setRefreshing(true);

      setErr('');
      try {
        const params = {
          page,
          per_page: meta.per_page || 5,
          search: filters.search || undefined,
          // сорт по дате
          sort_by: 'created_at',
          sort_dir: 'desc',
          order_by: 'created_at',
          direction: 'desc',
          _ts: Date.now(), // пробить кэш
        };

        const res = await axios.get('/backoffice/applications/grouped', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Accept-Language': langHeader,
          },
          params,
        });

        const { items, meta: metaFromServer } = normalizePayload(res);
        const approvedGroupIds = JSON.parse(localStorage.getItem('approvedGroups') || '[]');

        // список издательств
        const pubs = Array.from(
          new Set((items || []).map((g) => g.publisher_name).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b));
        publishers.current = pubs;

        // клиентские фильтры (publisher/decision)
        const filterAppsByDecision = (apps = []) => {
          if (filters.decision === 'return') return apps.filter((a) => a.decision === 'return');
          if (filters.decision === 'none') return apps.filter((a) => !a.decision);
          return apps;
        };

        let withApproval = (items || [])
          .map((g) => ({
            ...g,
            approved: approvedGroupIds.includes(g.id),
            _sort_ts: getGroupSortDate(g),
          }))
          .filter((g) => (filters.publisher ? (g.publisher_name || '') === filters.publisher : true))
          .map((g) => {
            const hasApps = Array.isArray(g.applications);
            if (!hasApps) return g;
            const filteredApps = filterAppsByDecision(g.applications);
            return { ...g, applications: filteredApps, applications_count: filteredApps.length };
          })
          .filter((g) => !Array.isArray(g.applications) || g.applications.length > 0);

        withApproval.sort((a, b) => (b._sort_ts || 0) - (a._sort_ts || 0));
        setGroups(withApproval);

        // состояние «раскрытости» групп
        if (preserveExpanded) {
          setExpanded((prev) => {
            const next = { ...prev };
            withApproval.forEach((g) => {
              if (typeof next[g.id] === 'undefined') next[g.id] = false;
            });
            localStorage.setItem('gal_expanded_groups', JSON.stringify(next));
            return next;
          });
        } else {
          const allClosed = {};
          withApproval.forEach((g) => (allClosed[g.id] = false));
          setExpanded(allClosed);
          localStorage.setItem('gal_expanded_groups', JSON.stringify(allClosed));
        }

        // meta
        if (metaFromServer) {
          setMeta({
            total: metaFromServer.total ?? 0,
            per_page: metaFromServer.per_page ?? 5,
            current_page: metaFromServer.current_page ?? page,
            last_page: metaFromServer.last_page ?? 1,
          });
        } else {
          setMeta((m) => ({ ...m, total: withApproval.length, current_page: 1, last_page: 1 }));
        }

        // показать фильтры если пусто при активных фильтрах
        if (withApproval.length === 0 && hasActiveFilters) setFiltersOpen(true);
      } catch (e) {
        const status = e?.response?.status;
        setErr(status === 403 ? t.forbidden : e?.response?.data?.message || e.message || t.error);
        setGroups([]);
      } finally {
        if (!preserveExpanded && !preserveScroll) setInitialLoading(false);
        else setRefreshing(false);
        if (preserveScroll) {
          requestAnimationFrame(() => window.scrollTo(0, lastScrollRef.current || 0));
        }
      }
    },
    [langHeader, t.forbidden, t.error, meta.per_page, filters, hasActiveFilters]
  );

  const softRefresh = useCallback(
    () => fetchGroups(meta.current_page || 1, { preserveExpanded: true, preserveScroll: true }),
    [fetchGroups, meta.current_page]
  );

  // ===== init =====
  useEffect(() => {
  // восстановить раскрытые группы…
  try {
    const saved = JSON.parse(localStorage.getItem('gal_expanded_groups') || '{}');
    if (saved && typeof saved === 'object') setExpanded(saved);
  } catch {}

  fetchUserProfile();
  // БЫЛО: preserveExpanded: true
  fetchGroups(1, { preserveExpanded: false, preserveScroll: false }); // ← так
}, [fetchUserProfile, fetchGroups]);


  // ===== expand/collapse =====
  const toggleGroup = (id) => {
    setExpanded((prev) => {
      const next = { ...prev, [id]: !(prev[id] ?? true) };
      localStorage.setItem('gal_expanded_groups', JSON.stringify(next));
      return next;
    });
  };

  const toggleAllGroups = () => {
    setExpanded((prev) => {
      const next = { ...prev };
      const target = !anyOpen;
      groups.forEach((g) => (next[g.id] = target));
      localStorage.setItem('gal_expanded_groups', JSON.stringify(next));
      return next;
    });
  };

  // ===== actions =====
  const handleApproveGroup = async (groupId) => {
    if (!groupId || approvingGroupId === groupId) return;

    const group = groups.find((g) => g.id === groupId);
    if (!group) return;

    const apps = Array.isArray(group.applications) ? group.applications : [];
    const hasNeedsReapproval = apps.some((a) => a.needs_reapproval);
    const allAppsPassRules = apps.every(isAppEligibleForApproval);

    let blockReason = '';
    if (!allAppsPassRules) {
      const bad = apps.find((a) => !isAppEligibleForApproval(a));
      const st = getStatusKey(bad?.status);
      const dk = getDecisionKey(bad?.decision);
      if (st === 'new' && !dk) blockReason = t.rule_new_no_decision;
      else if (st === 'back' && dk === 'return') blockReason = t.rule_back_return_block;
      else if (st === 'new' && dk !== 'accept' && dk !== 'return') blockReason = t.rule_new_only_acc_ret;
      else if (st === 'back' && dk !== 'no_expertise') blockReason = t.rule_back_only_noexp;
      else blockReason = t.rule_not_match;
    }

    if (hasNeedsReapproval) {
      alert(t.needs_reapproval);
      return;
    }
    if (!allAppsPassRules) {
      alert(blockReason);
      return;
    }
    if (!window.confirm(t.confirm)) return;

    setApprovingGroupId(groupId);
    try {
      await axios.post(
        `/ebmso/groups/${groupId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Accept-Language': langHeader } }
      );

      const ids = JSON.parse(localStorage.getItem('approvedGroups') || '[]');
      localStorage.setItem('approvedGroups', JSON.stringify(Array.from(new Set([...ids, groupId]))));

      alert(t.success);
      await softRefresh();
    } catch (e) {
      alert(e?.response?.data?.message || t.error);
    } finally {
      setApprovingGroupId(null);
    }
  };

  const goPage = (p) => {
    if (p < 1 || p > (meta.last_page || 1)) return;
    fetchGroups(p, { preserveExpanded: false, preserveScroll: true });
  };

  // ===== render =====
  if (initialLoading) return <p className="gal text--muted">{t.loading}</p>;
  if (err) return <p className="gal text--danger">{err}</p>;

  return (
    <div className="gal">
      {/* mini top refresher */}
      {refreshing && <div className="gal-refresh-line" aria-hidden />}

      {/* Фильтры */}
      <div className="filters">
        {filtersOpen && (
          <section className="gal-filters" role="region" aria-label="filters">
            <div className="filters-row">
              <div className="gal-field">
                <label className="gal-label">{t.searchGroup}</label>
                <input
                  className="gal-input"
                  placeholder="..."
                  value={filters.search}
                  onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                />
              </div>

              <div className="gal-field">
                <label className="gal-label">{t.publisherLabel}</label>
                <select
                  className="gal-select"
                  value={filters.publisher}
                  onChange={(e) => setFilters((f) => ({ ...f, publisher: e.target.value }))}
                >
                  <option value="">{t.any}</option>
                  {publishers.current.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              <div className="gal-field">
                <label className="gal-label">{locale === 'kz' ? 'Шешім' : 'Решение'}</label>
                <select
                  className="gal-select"
                  value={filters.decision}
                  onChange={(e) => setFilters((f) => ({ ...f, decision: e.target.value }))}
                >
                  <option value="all">{t.any}</option>
                  <option value="return">{t.returned}</option>
                  <option value="none">{t.noDecision}</option>
                </select>
              </div>
            </div>

            <div className="filters-actions">
              <button
                className="gal-btn"
                onClick={() => {
                  setFilters({ search: '', publisher: '', decision: 'all' });
                  fetchGroups(1, { preserveExpanded: false, preserveScroll: true });
                }}
              >
                {t.reset}
              </button>

              <button
                className="gal-btn gal-btn--primary"
                onClick={() => fetchGroups(1, { preserveExpanded: false, preserveScroll: true })}
              >
                {t.apply}
              </button>

              <button className="gal-btn" onClick={() => setFiltersOpen(false)}>
                {t.close}
              </button>
            </div>
          </section>
        )}
      </div>

      {/* тулбар */}
      <div className="gal-toolbar">
        <button className="gal-btn" onClick={toggleAllGroups}>
          {anyOpen
            ? (locale === 'kz' ? 'Барлығын жабу' : 'Свернуть все')
            : (locale === 'kz' ? 'Барлығын ашу' : 'Развернуть все')}
        </button>

        <button
          className="gal-btn gal-btn--link gal-filter-toggle"
          onClick={() => setFiltersOpen((v) => !v)}
          aria-expanded={filtersOpen}
        >
          🔎 {t.filters}
        </button>
      </div>

      {/* группы */}
      <div className="groups-wrap">
        {groups.length > 0 ? (
          groups.map((group) => {
            const apps = Array.isArray(group.applications) ? group.applications : [];

            const stat = apps.reduce(
              (acc, a) => {
                const c = classifyByStatusDecision(a);
                if (c.isAccepted) acc.accepted += 1;
                else if (c.isReturned) acc.returned += 1;
                else if (c.isNoExpertise) acc.noexp += 1;
                else if (c.needsDecision) acc.needs += 1;
                return acc;
              },
              { accepted: 0, returned: 0, noexp: 0, needs: 0 }
            );

            const stAccepted = stat.accepted;
            const stReturned = stat.returned;
            const stNoExp = stat.noexp;
            const stNeedsDec = stat.needs;
            const total = apps.length || 1;

            const isOpen = expanded[group.id] ?? false;

            const hasNeedsReapproval = apps.some((a) => a.needs_reapproval);
            const allAppsPassRules = apps.every(isAppEligibleForApproval);

            let blockReason = '';
            if (!allAppsPassRules) {
              const bad = apps.find((a) => !isAppEligibleForApproval(a));
              const st = getStatusKey(bad?.status);
              const dk = getDecisionKey(bad?.decision);
              if (st === 'new' && !dk) blockReason = t.rule_new_no_decision;
              else if (st === 'back' && dk === 'return') blockReason = t.rule_back_return_block;
              else if (st === 'new' && dk !== 'accept' && dk !== 'return') blockReason = t.rule_new_only_acc_ret;
              else if (st === 'back' && dk !== 'no_expertise') blockReason = t.rule_back_only_noexp;
              else blockReason = t.rule_not_match;
            }

            const canApprove = isDirector && allAppsPassRules && !hasNeedsReapproval;
            const whyDisabled = !isDirector ? '' : hasNeedsReapproval ? t.needs_reapproval : !allAppsPassRules ? blockReason : '';

            return (
              <div className={`group-section ${isOpen ? 'is-open' : 'is-closed'}`} key={group.id}>
                <div
                  className="group-header"
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleGroup(group.id)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleGroup(group.id)}
                  aria-expanded={isOpen}
                  aria-controls={`group-panel-${group.id}`}
                >
                  <span className="chev">{isOpen ? '▾' : '▸'}</span>
                  <span className="group-title">
                    {group.title || '—'}
                    {group.publisher_name && <span className="group-publisher"> — {group.publisher_name}</span>}
                  </span>

                  <div className="group-mini">
                    <span className="mini mini--ok" title={t.accepted}>✓ {stAccepted}</span>
                    <span className="mini mini--warn" title={t.returned}>↩ {stReturned}</span>
                    <span className="mini mini--danger" title={t.rejectedExpertise}>⛔ {stNoExp}</span>
                    <span className="mini mini--muted" title={t.decisionNeeded}>{t.decisionNeeded} {stNeedsDec}</span>
                  </div>

                  <span className="group-count">{t.apps}: {group.applications_count ?? apps.length}</span>

                  {isDirector && (
                    <div className="group-header-actions">
                      <button
                        className={`btn btn--primary btn-approve-inline${canApprove ? '' : ' is-disabled'}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (canApprove) handleApproveGroup(group.id);
                        }}
                        disabled={!canApprove || approvingGroupId === group.id}
                        title={canApprove ? '' : whyDisabled}
                      >
                        {approvingGroupId === group.id ? t.loading : t.approve}
                      </button>
                    </div>
                  )}
                </div>

                {isOpen && (
                  <div id={`group-panel-${group.id}`} className="group-panel">
                    <div className="group-progress" aria-label="progress">
                      <div className="bar bar--ok"     style={{ width: `${(stAccepted / total) * 100}%` }} />
                      <div className="bar bar--warn"   style={{ width: `${(stReturned / total) * 100}%` }} />
                      <div className="bar bar--danger" style={{ width: `${(stNoExp / total) * 100}%` }} />
                      <div className="bar bar--muted"  style={{ width: `${(stNeedsDec / total) * 100}%` }} />
                    </div>


                    <table className="custom-table">
                      <thead>
                        <tr>
                          <th>{t.publisher}</th>
                          <th>{t.category}</th>
                          <th>{t.book}</th>
                          <th>{t.grade}</th>
                          <th>{t.lang}</th>
                          <th>{t.service}</th>
                          <th>{t.status}</th>
                          <th>{locale === 'kz' ? 'Шешім' : 'Решение'}</th>
                          <th>{t.date}</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {apps.map((app) => {
                          const showDecisionBtn = needsDecision(app);
                          return (
                            <tr key={app.id} className={showDecisionBtn ? 'row--needs-decision' : ''}>
                              <td>{app.publisher || '—'}</td>
                              <td>{app.category || '—'}</td>
                              <td>{app.title}</td>
                              <td>{app.grade || '—'}</td>
                              <td>{app.development_language || '—'}</td>
                              <td>{app.service_type || '—'}</td>
                              <td>
                                <span className={`badge ${getStatusBadgeClass(app.status)}`}>{getStatusLabel(app.status)}</span>
                                {app.needs_reapproval && <div className="pill pill--reapprove">♻️ {t.needs_reapproval}</div>}
                              </td>
                              <td>
                                {(() => {
                                  const dk = getDecisionKey(app.decision);
                                  if (dk === 'accept')        return <span className="text--success fw-600">{t.accepted}</span>;
                                  if (dk === 'return')        return <span className="text--warn fw-600">{t.returned}</span>;
                                  if (dk === 'no_expertise')  return <span className="text--danger fw-600">{t.rejectedExpertise}</span>;
                                  return <span className="text--muted">{t.noDecision}</span>;
                                })()}
                              </td>
                              <td>
                                {app.created_at
                                  ? new Date(app.created_at).toLocaleDateString(locale === 'kz' ? 'kk-KZ' : 'ru-RU')
                                  : '—'}
                              </td>
                              <td className="cell-actions">
                                <button className="btn btn--ghost" onClick={() => setSelectedViewId(app.id)}>
                                  {t.view}
                                </button>
                                {showDecisionBtn && isGosService && !(getStatusKey(app.status) === 'new' && getDecisionKey(app.decision) === 'return') && (
  <button className="btn btn--secondary" onClick={() => setSelectedDecisionId(app.id)}>
    {t.makeDecision}
  </button>
)}

                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="gal-empty-underlist">
            <p className="text--muted">
              {locale === 'kz' ? 'Топтар табылмады' : 'Групп не найдено'}
            </p>
            <div className="empty-actions">
              <button className="gal-btn gal-btn--primary" onClick={() => setFiltersOpen(true)}>
                {locale === 'kz' ? 'Фильтрлерді ашу' : 'Открыть фильтры'}
              </button>
              <button
                className="gal-btn"
                onClick={() => {
                  setFilters({ search: '', publisher: '', decision: 'all' });
                  fetchGroups(1, { preserveExpanded: false, preserveScroll: true });
                }}
              >
                {locale === 'kz' ? 'Тазарту' : 'Сбросить фильтры'}
              </button>
            </div>
          </div>
        )}
      </div>

      {meta.last_page > 1 && (
        <div className="pager">
          <button className="btn" onClick={() => goPage(1)} disabled={meta.current_page === 1}>«</button>
          <button className="btn" onClick={() => goPage(meta.current_page - 1)} disabled={meta.current_page === 1}>‹</button>
          <span className="pager__info">{meta.current_page}/{meta.last_page}</span>
          <button className="btn" onClick={() => goPage(meta.current_page + 1)} disabled={meta.current_page === meta.last_page}>›</button>
          <button className="btn" onClick={() => goPage(meta.last_page)} disabled={meta.current_page === meta.last_page}>»</button>
        </div>
      )}

      {/* модалки */}
      {selectedViewId && (
        <div className="gal-modal-overlay" role="dialog" aria-modal="true">
          <div className="gal-modal-inner">
            <div className="gal-modal-header">
              <h3>{t.application}</h3>
              <button onClick={() => setSelectedViewId(null)} className="gal-modal-close" aria-label="Close">✖</button>
            </div>
            <ApplicationView
              applicationId={selectedViewId}
              mode="view"
              onClose={() => setSelectedViewId(null)}
              onChanged={softRefresh}
            />
          </div>
        </div>
      )}

      {selectedDecisionId && (
  <div className="gal-modal-overlay" role="dialog" aria-modal="true">
    <div className="gal-modal-inner">
      <div className="gal-modal-header">
        <h3>{t.makeDecision}</h3>
        <button onClick={() => setSelectedDecisionId(null)} className="gal-modal-close" aria-label="Close">✖</button>
      </div>
      <ApplicationView
        applicationId={selectedDecisionId}
        mode="decision"
        onClose={() => setSelectedDecisionId(null)}
        onChanged={() => {
          setSelectedDecisionId(null);   // закрыть модалку
          softRefresh();                  // обновить список
        }}
      />
    </div>
  </div>
)}

    </div>
  );
}
